import { useCallback, useMemo } from "react";
import { useOrderbook } from "../hooks/useOrderbook";
import { formatPrice, formatSize } from "../sdk/utils/display";
import {
  LoadingMessage,
  ErrorMessage,
  EmptyMessage,
  StatusIndicator,
  LayoutHeader,
} from "./smartlayout/fragments";
import { DEFAULT_HEIGHT_ORDERBOOK, DEFAULT_MAX_DEPTH } from "./smartlayout/constants";
import "./smartlayout/layout.css";

interface OrderbookProps {
  baseURL: string;
  wsURL: string;
  marketId: string;
  maxDepth?: number;
  height?: string;
  authToken?: string;
  walletAddress?: string;
  deviceID?: string;
}

function Orderbook({
  baseURL,
  wsURL,
  marketId,
  maxDepth = DEFAULT_MAX_DEPTH,
  height = DEFAULT_HEIGHT_ORDERBOOK,
  authToken,
  walletAddress,
  deviceID,
}: OrderbookProps): JSX.Element {
  const { orderbook, loading, error, isConnected, wsError } = useOrderbook({
    baseURL,
    wsURL,
    marketId,
    maxDepth,
    authToken,
    walletAddress,
    deviceID,
  });

  // Calculate max cumulative for depth visualization
  // Both asks and bids are descending (last element has highest cumulative)
  const maxCumulative = useMemo(() => Math.max(
    orderbook.asks.length > 0
      ? orderbook.asks[orderbook.asks.length - 1].cumulative
      : 0,
    orderbook.bids.length > 0
      ? orderbook.bids[orderbook.bids.length - 1].cumulative
      : 0,
  ), [orderbook.asks, orderbook.bids]);

  // Use display utilities for formatting (wrapped in useCallback for consistency)
  const formatPriceMemo = useCallback(
    (price: number): string => formatPrice(price),
    [],
  );

  const formatSizeMemo = useCallback(
    (size: number): string => formatSize(size),
    [],
  );

  const getDepthPercentage = useCallback((cumulative: number): number => {
    if (maxCumulative === 0) return 0;
    return (cumulative / maxCumulative) * 100;
  }, [maxCumulative]);

  if (loading) {
    return <LoadingMessage message="Loading orderbook..." />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="layout-container" style={{ height }}>
      {/* WebSocket Error Notice */}
      {wsError && (orderbook.asks.length > 0 || orderbook.bids.length > 0) && (
        <div className="orderbook-ws-warning">
          <img
            src="/caution-icon.svg"
            alt="Caution"
            className="orderbook-ws-warning-icon"
          />
          <span className="orderbook-ws-warning-text">
            {wsError}
          </span>
        </div>
      )}

      {/* Header */}
      <LayoutHeader title={<StatusIndicator isConnected={isConnected} />}>
        <div>Depth: {maxDepth}</div>
      </LayoutHeader>

      {/* Mid Price / Spread */}
      {orderbook.midPrice && (
        <div className="orderbook-mid-price">
          <div className="orderbook-mid-price-value">
            {formatPriceMemo(orderbook.midPrice)}
          </div>
          {orderbook.spread !== undefined && (
            <div className="orderbook-mid-price-spread">
              Spread: {formatPriceMemo(orderbook.spread)}
            </div>
          )}
        </div>
      )}

      {/* Orderbook Content */}
      <div className="orderbook-content">
        {/* Asks (Sell Orders) - Top Section */}
        <div className="orderbook-section">
          {orderbook.asks.map((level, index) => {
            const depthPercent = getDepthPercentage(level.cumulative);
            const flashClass =
              level.flash === "up"
                ? "flash-up"
                : level.flash === "down"
                  ? "flash-down-ask"
                  : "";
            return (
              <div
                key={`ask-${level.price}-${index}`}
                className={`orderbook-level ask ${flashClass}`}
                style={{
                  borderBottom:
                    index < orderbook.asks.length - 1
                      ? undefined
                      : "none",
                }}
              >
                {/* Depth bar background */}
                <div
                  className="orderbook-depth-bar ask"
                  style={{ width: `${depthPercent}%` }}
                />

                {/* Content */}
                <div className="orderbook-level-content ask">
                  {formatPriceMemo(level.price)}
                </div>
                <div className="orderbook-level-content right">
                  {formatSizeMemo(level.size)}
                </div>
                <div className="orderbook-depth-indicator">
                  <div className="orderbook-depth-line ask" />
                </div>
              </div>
            );
          })}
          {orderbook.asks.length === 0 && (
            <EmptyMessage message="No ask orders" />
          )}
        </div>

        {/* Bids (Buy Orders) - Bottom Section */}
        <div className="orderbook-section bids">
          {orderbook.bids.map((level, index) => {
            const depthPercent = getDepthPercentage(level.cumulative);
            const flashClass =
              level.flash === "up"
                ? "flash-up"
                : level.flash === "down"
                  ? "flash-down-bid"
                  : "";
            return (
              <div
                key={`bid-${level.price}-${index}`}
                className={`orderbook-level bid ${flashClass}`}
                style={{
                  borderBottom:
                    index < orderbook.bids.length - 1
                      ? undefined
                      : "none",
                }}
              >
                {/* Depth bar background */}
                <div
                  className="orderbook-depth-bar bid"
                  style={{ width: `${depthPercent}%` }}
                />

                {/* Content */}
                <div className="orderbook-level-content bid">
                  {formatPriceMemo(level.price)}
                </div>
                <div className="orderbook-level-content right">
                  {formatSizeMemo(level.size)}
                </div>
                <div className="orderbook-depth-indicator">
                  <div className="orderbook-depth-line bid" />
                </div>
              </div>
            );
          })}
          {orderbook.bids.length === 0 && (
            <EmptyMessage message="No bid orders" />
          )}
        </div>
      </div>

      {/* Footer - Market Depth Summary */}
      <div className="orderbook-footer">
        <div className="orderbook-footer-legend">
          <div className="orderbook-footer-bar bid" />
          <span className="color-text-muted">
            {(() => {
              // Both arrays are descending, so last element has highest cumulative
              const totalBids =
                orderbook.bids.length > 0
                  ? orderbook.bids[orderbook.bids.length - 1].cumulative
                  : 0;
              const totalAsks =
                orderbook.asks.length > 0
                  ? orderbook.asks[orderbook.asks.length - 1].cumulative
                  : 0;
              const total = totalBids + totalAsks;
              return total > 0
                ? `${((totalBids / total) * 100).toFixed(0)}%`
                : "0%";
            })()}
          </span>
        </div>
        <div className="orderbook-footer-legend">
          <div className="orderbook-footer-bar ask" />
          <span className="color-text-muted">
            {(() => {
              // Both arrays are descending, so last element has highest cumulative
              const totalBids =
                orderbook.bids.length > 0
                  ? orderbook.bids[orderbook.bids.length - 1].cumulative
                  : 0;
              const totalAsks =
                orderbook.asks.length > 0
                  ? orderbook.asks[orderbook.asks.length - 1].cumulative
                  : 0;
              const total = totalBids + totalAsks;
              return total > 0
                ? `${((totalAsks / total) * 100).toFixed(0)}%`
                : "0%";
            })()}
          </span>
        </div>
      </div>
    </div>
  );
}

export default Orderbook;
